import React, { useCallback } from 'react';
import { Copy } from '@phosphor-icons/react';

import { cn } from '../../lib/utils';
import { Tooltip, TooltipContent, TooltipTrigger } from '../ui/tooltip';
import { RippleStatus } from '../ui/ripple-status';
import { BlockRenderer } from './blocks';
import { getConversationStatusLabel } from './status';
import { useConversation } from './ConversationContext';
import type { Message } from './types';

interface ConversationMessageAssistantProps {
  message: Message;
  className?: string;
}

export const ConversationMessageAssistant = ({ message, className }: ConversationMessageAssistantProps) => {
  const { callbacks } = useConversation();
  const isStreaming = message.status === 'streaming';
  const isPending = message.status === 'pending';
  const isCompleted = message.status === 'completed';

  const handleCopy = useCallback(() => {
    const textContent = message.content
      .filter((b): b is { type: 'text'; text: string } => b.type === 'text')
      .map((b) => b.text)
      .join('\n');
    callbacks.onCopy?.(message.id, textContent);
  }, [message, callbacks]);

  const hasVisibleContent = message.content.some((block) => block.type === 'text' && block.text.length > 0);
  const isError = message.status === 'error';
  const isActive = isStreaming || isPending;

  // Live status derived from the latest content block. Shown for the entire
  // streaming duration ("latest only") and only disappears once the message
  // finishes streaming.
  const statusLabel = isActive ? getConversationStatusLabel(message) : null;

  // Before any answer text exists, the status stands alone (the initial
  // "Working…" / "Searching knowledge base" state).
  if (!hasVisibleContent && statusLabel) {
    return <RippleStatus text={statusLabel} className={className} />;
  }

  return (
    <div className={cn('flex gap-3', className)}>
      <div className="flex-1 min-w-0">
        <div className="space-y-1">
          {isError ? (
            <BlockRenderer block={{ type: 'text', text: 'Sorry, something went wrong. Please try again.' }} />
          ) : (
            message.content.map((block, i) => (
              <BlockRenderer
                key={i}
                block={block}
                isStreaming={isStreaming}
                onCitationClick={callbacks.onCitationClick}
              />
            ))
          )}
        </div>

        {statusLabel && <RippleStatus text={statusLabel} className="mt-2" />}

        {isCompleted && (
          <div className="mt-3 flex items-center gap-1">
            <ActionBarButton tooltip="Copy" onClick={handleCopy}>
              <Copy className="h-4 w-4" />
            </ActionBarButton>
          </div>
        )}
      </div>
    </div>
  );
};
ConversationMessageAssistant.displayName = 'ConversationMessageAssistant';
interface ActionBarButtonProps {
  children: React.ReactNode;
  tooltip: string;
  active?: boolean;
  onClick?: () => void;
}

const ActionBarButton = ({ children, tooltip, active, onClick }: ActionBarButtonProps) => {
  return (
    <Tooltip>
      <TooltipTrigger asChild>
        <button
          type="button"
          className={cn(
            'inline-flex h-7 w-7 items-center justify-center rounded-md text-muted-foreground transition-colors hover:bg-muted hover:text-foreground',
            active && 'text-primary',
          )}
          onClick={onClick}
        >
          {children}
        </button>
      </TooltipTrigger>
      <TooltipContent side="bottom">
        <p className="text-xs">{tooltip}</p>
      </TooltipContent>
    </Tooltip>
  );
};
ActionBarButton.displayName = 'ActionBarButton';